import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useAuthentication from '../Hooks/useAuthentication';
import useAddressValidation from "../Hooks/useAddressValidation";
import useTask from '../Hooks/useTask';
import ComponentFactory from '../Components/Factory/ComponentFactory';
import ModalComponent from '../Components/ModalComponent';
import { Box, Flex, Text, Image, Button, VStack, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from '@chakra-ui/react';
import NavBarComponent from '../Components/NavBarComponent';
import ButtonComponent from '../Components/ButtonComponent';
import loadingHeart from '../loadingHeart.gif';
import DOMPurify from 'dompurify';
import appConfig from '../Config/appConfig';
import PageViewTracker from '../Components/PageViewTracker';
import { Link } from 'react-router-dom';
import { isErrorDetailsEnabled } from '../Utils/telemetry';

const HomePage = () => {
const location = useLocation(); 
const params = new URLSearchParams(location.search);
const launchCode = params.get('SSLaunch');

const paramsObject = {};
params.forEach((value, key) => {
  paramsObject[key] = value;
});

const [showNavBar, setShowNavBar] = useState(true);
const [loadingTimer, setLoadingTimer] = useState(null);
const [delayedTaskLoading, setDelayedTaskLoading] = useState(false);
const [dynamicLongContent, setDynamicLongContent] = useState(false);
const [dynamicValidationState, setDynamicValidationState] = useState({});
const [currentStepHasRequiredFields, setCurrentStepHasRequiredFields] = useState(false);
const [errorDumpCopied, setErrorDumpCopied] = useState(false);

  // Function to check if current step has required fields
  const checkCurrentStepRequirements = () => {
    if (!sections || sections.length === 0) {
      setCurrentStepHasRequiredFields(false);
      return;
    }

    // Check if any section has required fields
    const hasRequiredFields = sections.some(section => {
      if (section.type === 'questionnaire' && section.questions) {
        return section.questions.some(question => question.required);
      }
      if (section.type === 'form' && section.fields) {
        return section.fields.some(field => field.required);
      }
      if (section.type === 'form' && section.required && Array.isArray(section.required)) {
        return section.required.length > 0;
      }
      if (section.type === 'form' && section.items) {
        return section.items.some(item => item.required);
      }
      return false;
    });

    setCurrentStepHasRequiredFields(hasRequiredFields);
  };
  
useEffect(() => {
  if (launchCode === '0') {
    setShowNavBar(true);
  } else {
    setShowNavBar(false);
  }
}, [launchCode]);

const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMouseDevice, setIsMouseDevice] = useState(false);

  useEffect(() => {
    const hasTouch =
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(any-pointer: coarse)').matches;

    const hasMouse = 'onmousemove' in window;

    setIsTouchDevice(hasTouch);
    setIsMouseDevice(hasMouse);

  }, []);

  const { loading: authLoading, authToken, message, authErrorDetails } = useAuthentication(location);
  const {
    loading: taskLoading,
    title,
    sections,
    isLongContent,
    error,
    errorDetails,
    readOnly,
    formData,
    setFormData,
    priorDecisions,
    handleNext,
    handleComplete,
    handleFinish,
    handleBack,
    handleSummary,
    handleSubmit,
    updateFormData,
  } = useTask(authToken, launchCode);
  const { validateAddress } = useAddressValidation();
  const background = sections?.find((section) => section.type === 'background');
  const { imageUrl } = background || {};
  useEffect(() => {
    if (taskLoading) {
      const timer = setTimeout(() => {
        setDelayedTaskLoading(taskLoading);
      }, 1500);
      setLoadingTimer(timer);
    } else {
      if (loadingTimer) {
        clearTimeout(loadingTimer);
        setLoadingTimer(null);
      }
      setDelayedTaskLoading(false);
    }

    return () => {
      if (loadingTimer) {
        clearTimeout(loadingTimer);
      }
    };
  }, [authLoading, taskLoading]);
  
  // Reset button states when step changes
  useEffect(() => {
    if (sections && sections.length > 0) {
      checkCurrentStepRequirements();
      setDynamicValidationState({});
    }
  }, [sections]);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const openModal = (content) => {
    setModalContent(content);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setModalContent(null);
    setIsModalOpen(false);
  };

  const handleDynamicLongContentChange = (hasLongContent) => {
    setDynamicLongContent(hasLongContent);
  };

  const handleDynamicValidationChange = (sectionId, isValid) => {
    setDynamicValidationState(prev => {
      const newState = {
        ...prev,
        [sectionId]: isValid
      };
      return newState;
    });
  };

  const handleButtonAction = async (action, buttonData) => {
    if (window.actionInProgress) {
      return;
    }
  
    try {
      window.actionInProgress = true;
      const { actionType, metadata, clearKeys, modalContent, url, deeplinkUrl=null } = buttonData;
      
      // Check if this is a "No Thanks" button and clear form fields
      const isNoThanksButton = buttonData?.label?.toLowerCase().includes('no thanks');
      if (isNoThanksButton) {
        // Clear only the remaining_eligible_programs field (current page selections)
        // Preserve other fields that might contain data from previous pages
        setFormData(prevFormData => {
          const clearedFormData = { ...prevFormData };
          
          // Only clear the remaining_eligible_programs field
          if (clearedFormData.remaining_eligible_programs) {
            delete clearedFormData.remaining_eligible_programs;
          }
          
          return clearedFormData;
        });
      }

      switch (action) {
        case 'modal':
          if (actionType === 'close') {
            closeModal();
            setTimeout(() => {
              window.close();
            }, 2000);
          } else {
            openModal(modalContent);
          }
          break;
        case 'next':
          let params;
          if (metadata) {
            params = { actionType, metadata, clearKeys }; // Send both if metadata exists
          } else if (actionType) {
            params = { actionType, clearKeys }; // Send actionType/clearKeys when provided
          } else {
            params = {}; // Ignore if no metadata and actionType is falsy
          }
          await handleNext(params);
          break;
        case 'finish':
          let paramsFinish;
          if (actionType) {
            paramsFinish = { actionType };
          } else {
            paramsFinish = {}; 
          }
          await handleFinish(paramsFinish);
          break;
        case 'back':
          // Don't clear formData when going back - preserve user's selections
          setDynamicValidationState({}); // Clear dynamic validation state when going back
          let backParams;
          if (metadata) {
            backParams = { actionType, metadata, clearKeys };
          } else if (actionType) {
            backParams = { actionType, clearKeys };
          } else {
            backParams = {};
          }
          await handleBack(backParams);

          // Apply local clear only after server-driven back navigation succeeds.
          if (String(actionType || '').trim().toLowerCase() === 'clear' && Array.isArray(clearKeys) && clearKeys.length > 0) {
            setFormData(prevFormData => {
              const clearedFormData = { ...prevFormData };
              clearKeys.forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(clearedFormData, key)) {
                  delete clearedFormData[key];
                }
              });
              return clearedFormData;
            });
          }
          break;
        case 'summary':
            setFormData({}); // Clear form data when going to summary
            setDynamicValidationState({}); // Clear dynamic validation state when going to summary
            await handleSummary({});
            break;
        case 'complete':
          if (actionType === 'onboarding' || actionType === 'health_score' || actionType === 'enrollment') {
            await handleComplete({ actionType, metadata }); // adding metadata for cuff_reorder
          } else {
            await handleComplete({});
          }
          break;
          
        case 'link':
            if (url || deeplinkUrl) {
              const linkToOpen = !showNavBar ? deeplinkUrl : url;
              window.location.href = linkToOpen;
            } else {
              console.error("URL or deeplinkUrl is required for 'openLink' action.");
            }
          break;
        
        case 'close':
          setTimeout(() => {
            window.close();
          }, 2000);
          break;
        case 'submit':
          if (formData || metadata) {
            if (actionType === 'shipping') {
              const getFieldValue = (field, metaField = field) =>
                formData?.[field]?.value || metadata?.[metaField] || '';
            
              const shippingData = {
                firstName: getFieldValue('firstName'),
                lastName: getFieldValue('lastName'),
                email: getFieldValue('emailAddress', 'email'),
                phoneNumber: getFieldValue('phoneNumber'),
                preferredLanguage: 'en_US',
                street: getFieldValue('street'),
                addressLine2: getFieldValue('addressLine2'),
                city: getFieldValue('city'),
                zipCode: getFieldValue('zipCode'),
                state: getFieldValue('state'),
                country: 'US',
              };
            
              const formMetadata = { ...metadata, ...shippingData };
            
              const { isValid, error } = await validateAddress(shippingData);
            
              if (!isValid) {
                console.error('Address validation failed:', error);
                openModal({
                  title: 'Invalid Address',
                  content: [{"type":"paragraph","content":"It appears the address you\'ve entered is invalid. Please double check your address and try again.","variant":"body-sm-min-reg"}],
                  actionLabel: 'Close',
                  onAction: closeModal,
                });
                return;
              }
            
              await handleNext({ actionType, metadata: formMetadata });
            }
             else if (actionType === 'ranking') {
              const getFieldValueArray = (field) =>
                Array.isArray(formData?.[field]?.value) ? formData[field].value : [];
        
              const rankingMetadata = {
                content_ranking: {
                  feedback: getFieldValueArray('tell_us_why'),
                  value: formData?.['how_was_it']?.value || '',
                  comments: formData?.['hear_more_comments']?.value || ''
                }
              };
        
              await handleSubmit({ actionType, metadata: rankingMetadata });
            } else {
              const submitParams = {};
              if (actionType) {
                submitParams.actionType = actionType;
              }
              if (metadata) {
                submitParams.metadata = metadata;
              }
              if (clearKeys) {
                submitParams.clearKeys = clearKeys;
              }
              await handleSubmit(submitParams);
            }
          }
          break;

        default:
          // Unhandled action type
      }
      document.activeElement.blur();
    } catch (error) {
      console.error(`Error handling action "${action}":`, error);
    } finally {
      window.actionInProgress = false;
    }
  };

  return (
    <><PageViewTracker pageName={title} />
      {showNavBar && <NavBarComponent />}
      <Flex direction="column" minHeight="100vh" bg={['white', 'gray.100']} mt={showNavBar ? 6 : 0}>
        {/* Main Content */}
      <Flex
        justify="center"
        align="center"
        height="100%"
        bg={readOnly ? "white" : ['white', 'gray.100']}
        py= {showNavBar ? 14 : 0}
      >
        { delayedTaskLoading || authLoading ? (
                <Flex justifyContent="center" mt={4}>
                  <Image src={loadingHeart} alt="Loading..." boxSize="50px"/> 
                </Flex>
              ): readOnly ? (
                <Box
                bg="white"
                width={['100%', '390px']}
                height='100%'
                display="flex"
                flexDirection="column"
                padding={2}
                mb={4}
                mt={showNavBar ? 6 : 0}>
                {sections.map((section, sectionIndex) => {
                  if (readOnly && section.type === 'progress') {
                    return null;
                  }
                  return (<ComponentFactory
                    key={section.id || `section-${sectionIndex}`}
                    section={section}
                    theme="light"
                    priorDecisions={priorDecisions}
                    formData={formData}
                    readOnly={readOnly}
                    updateFormData={updateFormData}
                    handleButtonAction={handleButtonAction}
                    onLongContentChange={handleDynamicLongContentChange}
                    onDynamicValidationChange={handleDynamicValidationChange} />);
                })}
              </Box>
                ):  message ? (
                  <Flex justifyContent="center" mt={4} direction="column" width="100%" maxWidth="390px" mx="auto">
                    <Text
                          mb={4}
                          textAlign="center"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message)}}
                        />
                    {isErrorDetailsEnabled() && authErrorDetails && (
                      <Box
                        mt={2}
                        textAlign="left"
                        bg="gray.800"
                        color="gray.100"
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="gray.600"
                        p={4}
                      >
                        <Flex justify="space-between" align="center" mb={3}>
                          <Text fontSize="sm" fontWeight="bold" color="orange.300">
                            Technical Details
                          </Text>
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="orange"
                            onClick={() => {
                              navigator.clipboard.writeText(JSON.stringify(authErrorDetails, null, 2));
                              setErrorDumpCopied(true);
                              setTimeout(() => setErrorDumpCopied(false), 2000);
                            }}
                          >
                            {errorDumpCopied ? 'Copied!' : 'Copy for support'}
                          </Button>
                        </Flex>
                        <VStack align="stretch" spacing={1.5} fontSize="sm" mb={3}>
                          <Flex><Text fontWeight="semibold" minWidth="110px">Stage</Text><Text>{authErrorDetails.stageLabel || authErrorDetails.operation || '—'}</Text></Flex>
                          <Flex><Text fontWeight="semibold" minWidth="110px">Status</Text><Text>{authErrorDetails.status ? `${authErrorDetails.status} ${authErrorDetails.statusText || ''}`.trim() : (authErrorDetails.isNetworkError ? 'Network error (no response)' : '—')}</Text></Flex>
                          <Flex><Text fontWeight="semibold" minWidth="110px">Error code</Text><Text fontFamily="mono">{authErrorDetails.errorCode || authErrorDetails.errorName || '—'}</Text></Flex>
                          <Flex><Text fontWeight="semibold" minWidth="110px">Message</Text><Text>{authErrorDetails.message || '—'}</Text></Flex>
                          <Flex><Text fontWeight="semibold" minWidth="110px">Request ID</Text><Text fontFamily="mono" fontSize="xs">{authErrorDetails.requestId || '—'}</Text></Flex>
                          <Flex><Text fontWeight="semibold" minWidth="110px">Time</Text><Text>{authErrorDetails.timestamp || '—'}</Text></Flex>
                        </VStack>
                        <Accordion allowToggle>
                          <AccordionItem border="none">
                            <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                              <Text fontSize="xs" color="gray.400" flex="1" textAlign="left">
                                Show full technical dump
                              </Text>
                              <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel px={0} maxHeight="320px" overflowY="auto">
                              <Text as="pre" fontSize="xs" whiteSpace="pre-wrap" wordBreak="break-word">
                                {JSON.stringify(authErrorDetails, null, 2)}
                              </Text>
                            </AccordionPanel>
                          </AccordionItem>
                        </Accordion>
                      </Box>
                    )}
                  </Flex>
                ): (
                  <Flex justify="center" align="center" width="100%">
                  <Box
                    flex="1"
                    display="flex"
                    flexDirection="column"
                    overflowY={(isLongContent || dynamicLongContent) ? "auto" : "visible"}
                    overflowX="hidden"
                    maxWidth="390px"
                    height={(isLongContent || dynamicLongContent) ? '100%' : '708px'}
                    bg={sections && sections.length > 0 ? 'white' : 'transparent'}
                    borderRadius={sections && sections.length > 0 ? ['none', 'md']: 'none'}
                    shadow={sections && sections.length > 0 ? ['none', '0px 4px 32px 0px #00000026'] : 'none'}
                    py={8}
                    px={4}
                    minHeight={"708px"}
                    backgroundImage={imageUrl ? `url(${appConfig.ImageBlobBaseUrl}/${imageUrl})` : 'none'}
                    backgroundSize="cover"
                    backgroundRepeat="no-repeat"
                    backgroundPosition="top left"
                  >
                      {error ? (
                        <Box textAlign="center" color="red.500" mt={4}>
                          <Text fontSize="lg" fontWeight="bold">
                          Something's wrong on our end — looks like we may need a check up. Try again later, and give us a call if it keeps happening. 
                          </Text>
                          <Text fontSize="sm">{error}</Text>
                          {isErrorDetailsEnabled() && errorDetails && (
                            <Box
                              mt={4}
                              textAlign="left"
                              bg="gray.800"
                              color="gray.100"
                              borderRadius="lg"
                              border="1px solid"
                              borderColor="gray.600"
                              p={4}
                            >
                              <Flex justify="space-between" align="center" mb={3}>
                                <Text fontSize="sm" fontWeight="bold" color="orange.300">
                                  Technical Details
                                </Text>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  colorScheme="orange"
                                  onClick={() => {
                                    navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
                                    setErrorDumpCopied(true);
                                    setTimeout(() => setErrorDumpCopied(false), 2000);
                                  }}
                                >
                                  {errorDumpCopied ? 'Copied!' : 'Copy for support'}
                                </Button>
                              </Flex>
                              <VStack align="stretch" spacing={1.5} fontSize="sm" mb={3}>
                                <Flex><Text fontWeight="semibold" minWidth="110px">Status</Text><Text>{errorDetails.status ? `${errorDetails.status} ${errorDetails.statusText || ''}`.trim() : (errorDetails.isNetworkError ? 'Network error (no response)' : '—')}</Text></Flex>
                                <Flex><Text fontWeight="semibold" minWidth="110px">Error code</Text><Text fontFamily="mono">{errorDetails.errorCode || errorDetails.errorName || '—'}</Text></Flex>
                                <Flex><Text fontWeight="semibold" minWidth="110px">Message</Text><Text>{errorDetails.message || '—'}</Text></Flex>
                                <Flex><Text fontWeight="semibold" minWidth="110px">Operation</Text><Text>{errorDetails.operation || '—'}</Text></Flex>
                                <Flex><Text fontWeight="semibold" minWidth="110px">Request ID</Text><Text fontFamily="mono" fontSize="xs">{errorDetails.requestId || '—'}</Text></Flex>
                                <Flex><Text fontWeight="semibold" minWidth="110px">Time</Text><Text>{errorDetails.timestamp || '—'}</Text></Flex>
                              </VStack>
                              <Accordion allowToggle>
                                <AccordionItem border="none">
                                  <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                                    <Text fontSize="xs" color="gray.400" flex="1" textAlign="left">
                                      Show full technical dump
                                    </Text>
                                    <AccordionIcon />
                                  </AccordionButton>
                                  <AccordionPanel px={0} maxHeight="320px" overflowY="auto">
                                    <Text as="pre" fontSize="xs" whiteSpace="pre-wrap" wordBreak="break-word">
                                      {JSON.stringify(errorDetails, null, 2)}
                                    </Text>
                                  </AccordionPanel>
                                </AccordionItem>
                              </Accordion>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        sections.map((section, sectionIndex) => {
                          if (!section.type) {
                            console.error(
                              `Missing type for item at Section ${sectionIndex}:`,
                              section
                            );
                            return (
                              <Text
                                key={`${sectionIndex}`}
                                color="red.500"
                                fontSize="sm"
                              >
                                Invalid item structure
                              </Text>
                            );
                          }
                          if (readOnly && section.type === 'progress') {
                            return null;
                          }
                          if (!readOnly && section.type === 'button' && Array.isArray(section.cta)) {
                            // Filter out select-all buttons from the bottom buttons
                            const filteredCta = section.cta.filter(ctaItem => ctaItem.action !== 'select-all');
                            const hasHalfVariant = filteredCta.some(ctaItem => ctaItem.variant?.includes('-half'));
                          
                            const Wrapper = hasHalfVariant ? Flex : Box;
                            const wrapperProps = hasHalfVariant ? { justify: "space-between", gap: 4, width: "100%" } : {};
                            
                            // SIMPLIFIED: Only disable buttons when we actually have validation failures
                            const hasValidationFailures = Object.keys(dynamicValidationState).length > 0 && 
                              Object.values(dynamicValidationState).some(isValid => !isValid);
                          
                            return (
                              <Wrapper key="btns-wrapper" {...wrapperProps}>
                                {filteredCta.map((ctaItem, btnIndex) => (
                                  <ButtonComponent
                                    key={`cta-${btnIndex}`}
                                    action={ctaItem.action}
                                    label={ctaItem.label}
                                    ariaLabel={ctaItem.ariaLabel}
                                    tooltip={ctaItem.tooltip}
                                    variant={ctaItem.variant || 'solid'}
                                    marginBottom={ctaItem.marginBottom}
                                    icons={ctaItem.icons}
                                    onClick={() => handleButtonAction(ctaItem.action, ctaItem)}
                                    textDecoration={ctaItem.textDecoration}
                                    isTouchDevice={isTouchDevice}
                                    isMouseDevice={isMouseDevice}
                                     isDisabled={
                                       // ONLY disable next/submit buttons when we have actual validation failures
                                       // Exception: "No Thanks" buttons should always be enabled (optional actions)
                                       (ctaItem?.action === 'next' || ctaItem?.action === 'submit') 
                                         ? hasValidationFailures && !(ctaItem?.label?.toLowerCase().includes('no thanks'))
                                         : false
                                     } />
                                ))}
                              </Wrapper>
                            );
                          }               
                          
                          return (
                            <ComponentFactory
                              key={section.id || `section-${sectionIndex}`}
                              section={section}
                              theme="light"
                              priorDecisions={priorDecisions}
                              formData={formData}
                              readOnly={readOnly}
                              updateFormData={updateFormData}
                              handleButtonAction={handleButtonAction}
                              onLongContentChange={handleDynamicLongContentChange}
                              onDynamicValidationChange={handleDynamicValidationChange}
                            />
                          );
                        })
                      )}
                    </Box>
                    </Flex>
                )}
      </Flex>
      {/* Modal Component */}
      <ModalComponent
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        modalContent={modalContent}
        handleButtonAction={handleButtonAction}
      />


    </Flex>
    </>
  );
};

export default HomePage;